import React, { useState } from 'react';
import { Modal, LockboxModalHeader } from '../ui';
import Button from '../ui/Button';
import Alert from '../ui/Alert';
import { TextInput } from '../ui/Input';
import { createGroup } from '../../api/groupService';
import LeagueCreatedSuccessModal from './LeagueCreatedSuccessModal';

const SPORTS = ['NBA', 'NFL', 'MLB', 'NHL', 'SOCCER', 'MMA', 'BOXING', 'NCAA'];

export default function CreateGroupModal({ open, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [sport, setSport] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [createdLeague, setCreatedLeague] = useState(null);

  const resetForm = () => {
    setName('');
    setDescription('');
    setSport('');
    setError('');
  };

  const handleClose = () => {
    if (loading) return;
    resetForm();
    setCreatedLeague(null);
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const result = await createGroup({
        name: name.trim(),
        description: description.trim(),
        sport: sport || null,
      });
      setCreatedLeague({
        groupId: result.groupId,
        groupName: name.trim(),
      });
      resetForm();
    } catch (err) {
      setError(err?.message || 'Could not create group');
    } finally {
      setLoading(false);
    }
  };

  const handleGoToLeague = (groupId) => {
    setCreatedLeague(null);
    onClose();
    onCreated?.(groupId);
  };

  return (
    <>
    <LeagueCreatedSuccessModal
      open={Boolean(createdLeague)}
      groupId={createdLeague?.groupId}
      groupName={createdLeague?.groupName}
      onGoToLeague={handleGoToLeague}
    />
    <Modal
      open={open && !createdLeague}
      onClose={handleClose}
      accent="admin"
      header={(
        <LockboxModalHeader
          emoji="🏆"
          iconClassName="bg-violet-500/10 border-violet-500/20"
          title="Create league"
          onClose={handleClose}
        />
      )}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <p className="text-[10px] font-black uppercase tracking-widest text-gray-500 mb-2">League name</p>
          <TextInput
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Office NFL League"
            maxLength={80}
            required
            className="mt-0"
            focus="admin"
          />
        </div>
        <div>
          <p className="text-[10px] font-black uppercase tracking-widest text-gray-500 mb-2">Description (optional)</p>
          <TextInput
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Weekly head-to-head locks with the crew"
            maxLength={280}
            className="mt-0"
            focus="admin"
          />
        </div>
        <div>
          <p className="text-[10px] font-black uppercase tracking-widest text-gray-500 mb-2">Sport (optional)</p>
          <select
            value={sport}
            onChange={(e) => setSport(e.target.value)}
            className="w-full rounded-xl border border-gray-800 bg-black/50 px-3 py-2.5 text-sm font-bold text-white"
          >
            <option value="">Any</option>
            {SPORTS.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        {error && <Alert tone="error" className="text-xs font-bold">{error}</Alert>}
        <div className="flex gap-2 justify-end">
          <Button type="button" variant="ghost" size="sm" onClick={handleClose}>Cancel</Button>
          <Button type="submit" variant="admin" size="sm" disabled={loading || !name.trim()}>
            {loading ? 'Creating…' : 'Create league'}
          </Button>
        </div>
      </form>
    </Modal>
    </>
  );
}
